import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useGetCandidateQuery,
  useApproveCandidateMutation,
  useRejectCandidateMutation,
  type EventCandidate,
} from '../../redux/candidates/candidates.api';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

// Reuse existing, semantically-colored badge classes (green / red / neutral).
const STATUS_BADGE: Record<string, string> = {
  pending: 'user-detail__badge',
  approved: 'user-detail__badge user-detail__badge--verified',
  rejected: 'user-detail__badge user-detail__badge--banned',
};

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Action failed';
};

const formatDateTime = (
  dateIso: string | null,
  time: string | null,
): string => {
  if (!dateIso) return '—';
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return dateIso;
  const dateStr = d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return time ? `${dateStr} · ${time}` : dateStr;
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps): React.JSX.Element => (
  <div className="user-detail__field">
    <span className="user-detail__field-label">{label}</span>
    <span className="user-detail__field-value">{value || '—'}</span>
  </div>
);

// LLM-backed sources (telegram, kompas) store what the model extracted plus a
// snapshot of the original post, so a reviewer can catch a hallucinated date
// or price before approving. Other sources store their raw node (JSON-LD,
// goout feeder item) — still occasionally useful, so the full JSON is always
// available behind a <details>.
interface LlmPayload {
  extracted?: Record<string, unknown>;
  telegramPost?: { url?: string; publishedAt?: string };
}

const RawPayloadSection = ({
  payload,
}: {
  payload: unknown;
}): React.JSX.Element | null => {
  if (payload == null) return null;
  const llm = (typeof payload === 'object' ? payload : {}) as LlmPayload;
  const extracted =
    llm.extracted && typeof llm.extracted === 'object' ? llm.extracted : null;

  return (
    <section className="user-detail__card user-detail__card--wide">
      <h3 className="user-detail__card-title">Extracted data</h3>
      {extracted ? (
        <>
          <p className="user-detail__empty" style={{ marginBottom: 8 }}>
            Fields below were extracted by the LLM from the source text — check
            the date, price and venue against the original before approving.
          </p>
          {llm.telegramPost?.url && (
            <Field
              label="Original post"
              value={
                <a
                  className="user-detail__link"
                  href={llm.telegramPost.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open on Telegram ↗
                </a>
              }
            />
          )}
          {Object.entries(extracted)
            .filter(([key]) => key !== 'sourceId')
            .map(([key, value]) => (
              <Field
                key={key}
                label={key}
                value={
                  value == null
                    ? null
                    : typeof value === 'string'
                      ? value
                      : String(value)
                }
              />
            ))}
        </>
      ) : (
        <p className="user-detail__empty">
          Raw payload captured from the source (no LLM extraction for this
          source).
        </p>
      )}
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13 }}>
          Full raw payload (JSON)
        </summary>
        <pre
          style={{
            marginTop: 8,
            padding: 12,
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          {JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </section>
  );
};

export const AdminCandidateDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const {
    data: c,
    isLoading,
    error,
  } = useGetCandidateQuery(id ?? '', { skip: !id });
  const [approve, { isLoading: isApproving }] = useApproveCandidateMutation();
  const [reject, { isLoading: isRejecting }] = useRejectCandidateMutation();
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return <div className="admin-users__loading">Loading candidate…</div>;
  }

  if (error || !c) {
    return (
      <div className="user-detail">
        <Link to="/admin/dashboard/candidates" className="user-detail__back">
          ← Back to candidates
        </Link>
        <div className="admin-users__error">
          {error && 'status' in error && error.status === 404
            ? 'Candidate not found.'
            : 'Failed to load candidate.'}
        </div>
      </div>
    );
  }

  const candidate: EventCandidate = c;
  const start = formatDateTime(candidate.startDate, candidate.startTime);
  const end = formatDateTime(candidate.endDate, candidate.endTime);
  const isPending = candidate.status === 'pending';

  const handleApprove = async (): Promise<void> => {
    if (
      !window.confirm(
        `Approve "${candidate.name}"?\n\nThis publishes a live event authored by the ingestion account and geocodes its location.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await approve(candidate.id).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const handleReject = async (): Promise<void> => {
    if (
      !window.confirm(
        `Reject "${candidate.name}"?\n\nIt stays hidden and won't resurface on the next ingestion run.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await reject(candidate.id).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  return (
    <div className="user-detail">
      <Link to="/admin/dashboard/candidates" className="user-detail__back">
        ← Back to candidates
      </Link>

      <div className="event-detail__hero">
        <div className="event-detail__hero-image">
          {candidate.imageUrl ? (
            <img src={candidate.imageUrl} alt="" />
          ) : (
            <div className="event-detail__hero-image-blank" />
          )}
        </div>
        <div className="event-detail__hero-info">
          <h2 className="user-detail__name">{candidate.name}</h2>
          <div className="user-detail__badges">
            <span
              className={STATUS_BADGE[candidate.status] || 'user-detail__badge'}
            >
              {STATUS_LABEL[candidate.status] || candidate.status}
            </span>
            <span className="user-detail__badge user-detail__badge--verified">
              {candidate.source}
            </span>
            {candidate.category && (
              <span className="user-detail__badge">{candidate.category}</span>
            )}
          </div>
          <div className="event-detail__hero-meta">
            <div>{start}</div>
            {candidate.location?.city && <div>📍 {candidate.location.city}</div>}
          </div>
        </div>
      </div>

      <section className="user-detail__card user-detail__card--actions">
        <h3 className="user-detail__card-title">Review</h3>
        {isPending ? (
          <div className="user-detail__action-row user-detail__action-row--buttons">
            <button
              type="button"
              className="user-detail__action-btn user-detail__action-btn--unban"
              onClick={handleApprove}
              disabled={isApproving || isRejecting}
            >
              {isApproving ? 'Approving…' : 'Approve → publish'}
            </button>
            <button
              type="button"
              className="user-detail__action-btn user-detail__action-btn--ban"
              onClick={handleReject}
              disabled={isApproving || isRejecting}
            >
              {isRejecting ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        ) : candidate.status === 'approved' ? (
          <p className="user-detail__empty">
            Approved.{' '}
            {candidate.approvedEventId ? (
              <Link
                className="user-detail__link"
                to={`/admin/dashboard/events/${candidate.approvedEventId}`}
              >
                View live event ↗
              </Link>
            ) : (
              'The live event was created.'
            )}
          </p>
        ) : (
          <p className="user-detail__empty">
            Rejected — hidden and excluded from future runs.
          </p>
        )}
        {actionError && (
          <div className="admin-users__error" style={{ marginTop: 12 }}>
            {actionError}
          </div>
        )}
      </section>

      <div className="user-detail__grid">
        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Description</h3>
          {candidate.description ? (
            <p className="event-detail__description">{candidate.description}</p>
          ) : (
            <p className="user-detail__empty">No description.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">When</h3>
          <Field label="Starts" value={start} />
          <Field label="Ends" value={end} />
          <Field label="Timezone" value={candidate.timezone} />
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Where</h3>
          <Field label="Address" value={candidate.location?.address} />
          <Field label="City" value={candidate.location?.city} />
          <Field label="Country" value={candidate.location?.country} />
          {candidate.location?.latitude != null &&
          candidate.location?.longitude != null ? (
            <Field
              label="Coords"
              value={
                <span className="user-detail__coords">
                  {candidate.location.latitude.toFixed(5)},{' '}
                  {candidate.location.longitude.toFixed(5)}
                </span>
              }
            />
          ) : (
            <Field
              label="Coords"
              value="Geocoded at approval from the address."
            />
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Details</h3>
          <Field
            label="Fee"
            value={
              candidate.fee != null
                ? `${candidate.fee}${candidate.currency ? ` ${candidate.currency}` : ''}`
                : 'Free / unknown'
            }
          />
          <Field label="Category" value={candidate.category} />
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Source</h3>
          <Field label="Source" value={candidate.source} />
          <Field label="External ID" value={candidate.externalId} />
          <Field
            label="Source URL"
            value={
              candidate.sourceUrl ? (
                <a
                  className="user-detail__link"
                  href={candidate.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open ↗
                </a>
              ) : null
            }
          />
          <Field
            label="Ticket URL"
            value={
              candidate.ticketUrl ? (
                <a
                  className="user-detail__link"
                  href={candidate.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open ↗
                </a>
              ) : null
            }
          />
          <Field
            label="First seen"
            value={new Date(candidate.firstSeenAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          />
          <Field
            label="Last seen"
            value={
              candidate.lastSeenAt
                ? new Date(candidate.lastSeenAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : null
            }
          />
        </section>

        <RawPayloadSection payload={candidate.rawPayload} />
      </div>
    </div>
  );
};
